import api from "../api";
import type { RouteMapItem } from "../types/routes";
import { filterRoutes } from "../utils/routeFilters";
import { pointToPolylineM } from "./geo";
import { pickDisplayName } from "./names";
import type { LatLon, NearbyResult, PlaceHit, PoiHit, RouteHit, SearchProvider, SearchResult } from "./types";

export const NEAR_ROUTE_RADIUS_M = 2000;
export const NEARBY_POI_RADIUS_M = 5000;
const MIN_QUERY = 2;
const SEARCH_LIMIT = 20;

interface PlaceDto {
  id: string; kind: string; name: string | null; name_ru: string | null; name_en: string | null; name_kk: string | null;
  lat: number; lon: number; distance_m: number | null; ele?: number | null; difficulty?: string | null;
}

const toBase = (d: PlaceDto) => ({
  id: d.id,
  name: pickDisplayName(d),
  names: { ru: d.name_ru, en: d.name_en, kk: d.name_kk },
  osmKind: d.kind,
  lat: d.lat,
  lon: d.lon,
  ...(d.ele != null ? { ele: d.ele } : {}),
  ...(d.difficulty ? { difficulty: d.difficulty } : {}),
});
export const toPlaceHit = (d: PlaceDto): PlaceHit => ({ kind: "place", ...toBase(d) });
export const toPoiHit = (d: PlaceDto): PoiHit => ({ kind: "poi", ...toBase(d) });

// preview_line приходит как [lon, lat]; весь UI работает с [lat, lon].
// Без линии - одна точка старта, чтобы расстояние всё же считалось.
const toRouteHit = (r: RouteMapItem): RouteHit => {
  const line: [number, number][] = r.preview_line?.length
    ? r.preview_line.map(([lon, lat]) => [lat, lon])
    : r.start_lat != null && r.start_lon != null ? [[r.start_lat, r.start_lon]] : [];
  return {
    kind: "route", id: `route:${r.id}`, routeId: r.id, name: r.name, verified: r.owner_user_id == null,
    distanceKm: r.distance_km ?? 0, durationMin: r.estimated_duration_minutes ?? 0, grade: r.technical_grade ?? 0, line,
  };
};

export function createApiProvider(getRoutes: () => RouteMapItem[]): SearchProvider {
  return {
    async search(query, { origin }): Promise<SearchResult> {
      const q = query.trim();
      if (q.length < MIN_QUERY) return { places: [], routes: [], pois: [] };
      const routes = filterRoutes(getRoutes(), { query: q }).map(toRouteHit);
      try {
        const { data } = await api.get<{ places: PlaceDto[]; pois: PlaceDto[] }>("/search", {
          params: { q, lat: origin.lat, lon: origin.lon, limit: SEARCH_LIMIT },
        });
        return { places: data.places.map(toPlaceHit), routes, pois: data.pois.map(toPoiHit) };
      } catch {
        // Сеть/бэкенд недоступны - локальные маршруты всё равно показываем.
        return { places: [], routes, pois: [], failed: true };
      }
    },

    async nearby(point: LatLon): Promise<NearbyResult> {
      const { data } = await api.get<{ pois: PlaceDto[] }>("/search/nearby", {
        params: { lat: point.lat, lon: point.lon, radius_m: NEARBY_POI_RADIUS_M },
      });
      const routes = getRoutes()
        .map(toRouteHit)
        .map((r) => ({ r, d: pointToPolylineM(point, r.line) }))
        .filter((x) => x.d <= NEAR_ROUTE_RADIUS_M)
        .sort((a, b) => a.d - b.d)
        .map((x) => ({ ...x.r, lineDistanceM: x.d }));
      return { routes, pois: data.pois.map(toPoiHit) };
    },
  };
}

// GET /search/points/{id}: восстановить выбранную точку по ссылке/истории.
export async function fetchPoint(id: string): Promise<PlaceHit | PoiHit | null> {
  try {
    const { data } = await api.get<PlaceDto>(`/search/points/${encodeURIComponent(id)}`);
    return id.startsWith("place:") ? toPlaceHit(data) : toPoiHit(data);
  } catch (e) {
    if ((e as { response?: { status?: number } }).response?.status === 404) return null;
    throw e;
  }
}
